// Pre-match countdown: the big 3 → 2 → 1 → «В бой!» over the stage, and the black-and-white frame that
// fades back to colour when play starts (engrave.ts's saturation uniform). Driven once a frame by
// matchView.ts from GameView.countdown, the same way for offline and online play.

import { t } from './i18n/index.js';

export const GO_FLASH_S = 0.8;    // how long «В бой!» stays up once the countdown hits 0
export const COLOUR_FADE_S = 0.6; // black and white back to full colour

export type CountdownFrame = { text: string | null; saturation: number };

/** `countdown`: seconds left (0 once playing). `sinceStart`: seconds since it reached 0, or null when this
    match never showed a countdown (a reconnect into a running match) — then nothing is drawn. */
export function countdownFrame(countdown: number, sinceStart: number | null): CountdownFrame {
  if (countdown > 0) return { text: String(Math.ceil(countdown)), saturation: 0 };
  if (sinceStart === null) return { text: null, saturation: 1 };
  return { text: sinceStart < GO_FLASH_S ? t('countdown.go') : null, saturation: Math.min(1, sinceStart / COLOUR_FADE_S) };
}

export function createCountdown(stageEl: HTMLElement, setSaturation: (k: number) => void) {
  const el = document.createElement('div');
  el.className = 'countdown';
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'assertive');
  el.hidden = true;
  stageEl.appendChild(el);
  let sinceStart: number | null = null;
  let shown: string | null = null;
  let saturation = -1;

  function update(countdown: number, dt: number) {
    if (countdown > 0) sinceStart = 0;
    else if (sinceStart !== null) sinceStart += dt;
    const f = countdownFrame(countdown, sinceStart);
    if (f.saturation !== saturation) { saturation = f.saturation; setSaturation(saturation); }
    if (f.text === shown) return;
    shown = f.text;
    el.hidden = shown === null;
    if (shown === null) return;
    el.textContent = shown;
    el.classList.toggle('go', countdown <= 0);
    // restart the entrance animation for every new digit
    el.classList.remove('tick');
    void el.offsetWidth;
    el.classList.add('tick');
  }

  function destroy() {
    el.remove();
    setSaturation(1);  // the engraver outlives the match
  }

  return { update, destroy };
}
